import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import { ReactComponent as TrendDownIcon } from '../../../assets/icons/trend-down.svg';
import { ReactComponent as TrendUpIcon } from '../../../assets/icons/trend-up.svg';
import { useWebSocket } from '../../../contexts/WebSocketV2/WebSocketProvider';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  fetchChargingStationList,
  setSingleStationPerformance,
  setStationPerformance,
  fetchAvgOnlineOfflineDetails,
} from '../../../redux/slices/StationManagementSidePanelSlice';
import { RootState } from '../../../redux/store';
import SelectField from '../../../shared-components/select-field/SelectField';
import TabToggle from '../../../shared-components/tab-toggle/TabToggle';
import stationManagement from '../../../utils/api/station-management';
import HardwareStats from '../StationPerformance/HardwareStats';
// import HealthIndexTrendChart from '../StationPerformance/HealthIndexTrendChart';
import HeatMapByHour from '../StationPerformance/HeatMapByHour';
import HistoricalFaults from '../StationPerformance/HistoricalFaults';
import SoftWareLogs from '../StationPerformance/SoftWareLogList';
import StationStatusDistributionChart from '../StationPerformance/StationStatusDistributionChart';
import UptimeStationChart from '../StationPerformance/UptimeStationChart';

// import MeterChart from './MeterChart';

interface StationValues {
  selectedDevice: string;
  selectedStation: string;
}

/**
 * Station performance component
 * @returns {any} The rendered component
 */
function StationPerformance() {
  const { t, i18n } = useTranslation('sidePanel');
  const { connectWebSocket } = useWebSocket();
  const { messages } = useAppSelector((state) => state.ocppMessageV2);
  const { currentPage } = useParams<{ currentPage: string }>();
  const location = useLocation();
  const moduleName = location.pathname.split('/')[2];
  const dispatch = useAppDispatch();
  const { selectedChargingPoint, chargingStationList, onlineAvgHours, offlineTotalHours } =
    useAppSelector((state: RootState) => state.stationManagement);
  const [selectedTab, setSelectedTab] = useState<string>('');
  const [stationList, setStationList] = useState<{ label: string; value: string }[]>([]);
  const [deviceOptions, setDeviceOptions] = useState<{ label: string; value: string }[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<string | number>('');

  const [StationValues, setStationValues] = useState<StationValues>({
    selectedStation: 'all',
    selectedDevice: '',
  });

  /** Handle device selection */
  const handleChangeFunction = (name: string, value: string) => {
    setStationValues((previous) => ({ ...previous, [name]: value }));
  };

  useEffect(() => {
    if (moduleName === 'charging-station') {
      if (currentPage === 'unapproved') {
        dispatch(fetchChargingStationList({ submissionStatus: 'pending' }));
      } else {
        dispatch(fetchChargingStationList({ submissionStatus: `${currentPage}` }));
      }
    } else {
      dispatch(fetchChargingStationList({ submissionStatus: 'approved' }));
    }
  }, []);

  useEffect(() => {
    if (chargingStationList.length > 0) {
      const formattedStations = chargingStationList.map(
        (station: { name: string; _id: string }) => ({
          label: station.name,
          value: station._id,
        }),
      );

      setStationList([{ label: t('allStation'), value: 'all' }, ...formattedStations]);
    }
  }, [chargingStationList, t]);

  useEffect(() => {
    dispatch(
      setStationPerformance({
        id: StationValues.selectedStation,
      }),
    );
    dispatch(
      fetchAvgOnlineOfflineDetails({
        chargingStation: StationValues.selectedStation,
      }),
    );
  }, [StationValues]);
  /**
   *  connect web Socket
   */
  const handleConnect = () => {
    if (selectedDevice) {
      connectWebSocket(selectedDevice); // automatically disconnects old and connects new
    }
  };

  /**
   * Handle dropdown change
   * @param {string} _name The field name
   * @param {string | number | (string | number)[]} sn The selected value
   */
  const handleChangeDevice = (_name: string, sn: string | number | (string | number)[]) => {
    const selected = Array.isArray(sn) ? sn[0] : sn;
    setSelectedDevice(selected);
  };

  /**
   * Fetch device list
   */
  const handleDevicesList = async () => {
    if (!selectedChargingPoint) return;

    try {
      const response = await stationManagement.fetchDeviceList({
        chargingStation: selectedChargingPoint?.id,
      });

      const list = response.data || [];
      const formatted = list.map((item: any) => ({
        label: item.chargingPointName || item.chargingPointSn || t('unnamedDevice'),
        value: item.chargingPointSn,
      }));

      setDeviceOptions(formatted);

      // Auto-select first device
      if (formatted.length > 0) {
        handleChangeDevice('value', formatted[0].value);
      }
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        },
      });
    }
  };

  useEffect(() => {
    handleDevicesList();
  }, [selectedChargingPoint]);
  useEffect(() => {
    handleConnect();
  }, [selectedDevice]);

  useEffect(() => {
    dispatch(
      setSingleStationPerformance({
        id: selectedDevice,
      }),
    );
  }, [selectedDevice]);

  /**
   * Convert websocket messages into connector slides
   */
  const connectorSlides = messages
    .filter((message: any) => message.event === 'RealTime Charging Monitoring')
    .map((message: any) => ({
      chargingSpeed: message.chargingSpeed || 0,
    }));
  return (
    <div className="station-performance-container" dir={i18n.dir()}>
      <div className="station-performance-heading">
        <div className="select-station">
          <SelectField
            options={stationList}
            placeholder={t('selectStation')}
            selectedValue={StationValues.selectedStation}
            name="selectedStation"
            onChangeValue={handleChangeFunction}
            enableSearch
          />
        </div>
        <div className="time-content">
          <div className="time-card uptime">
            <div className="icon-box">
              <TrendUpIcon />
            </div>
            <div className="text-box">
              <p className="time-value">
                {onlineAvgHours} {t('hrs')}
              </p>
              <p className="time-label">{t('avgUptimeAcrossAllStations')}</p>
            </div>
          </div>
          <div className="time-card downtime">
            <div className="icon-box">
              <TrendDownIcon />
            </div>
            <div className="text-box">
              <p className="time-value">
                {offlineTotalHours} {t('hr')}
              </p>
              <p className="time-label">{t('totalDowntimeHoursThisMonth')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="chart-container">
        {/* <HealthIndexTrendChart /> */}
        <StationStatusDistributionChart />
        <UptimeStationChart />
        <HeatMapByHour />
      </div>

      <div className="tab-toggle-heading">
        <TabToggle
          tabs={[t('hardwareStats'), t('softwareLogs'), t('historicalFaults')]}
          defaultTab={t('hardwareStats')}
          onChange={(tab) => setSelectedTab(tab)}
        />
      </div>
      <div className="select-field">
        <SelectField
          options={deviceOptions}
          placeholder={t('selectDevice')}
          selectedValue={selectedDevice}
          name="selectedStation"
          onChangeValue={handleChangeDevice}
        />
      </div>
      {selectedTab === t('hardwareStats') && (
        <div className="hardware-stats">
          <div className="hardware-stats-main">
            <div className="hardware-stats-container">
              <HardwareStats
                value={connectorSlides[0]?.chargingSpeed || 0.0}
                label={t('chargingVoltage')}
                units={` ${t('voltage')}`}
              />
              <div className="chart-tittle">{t('chargingVoltage')}</div>
            </div>
          </div>
        </div>
      )}

      {selectedTab === t('softwareLogs') && (
        <div className="soft-ware-logs">
          <SoftWareLogs />
        </div>
      )}

      {selectedTab === t('historicalFaults') && <HistoricalFaults />}
    </div>
  );
}

export default StationPerformance;
